import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';
import { getBaseUrl } from '@/services/httpClient';
import { unlockAudioOnFirstTap, playAudioSafely, isIOS, logAudioDiagnostics, monitorAudioPlayback } from '@/services/iosAudioHelper';

/**
 * JourneyIntro - ÉCRAN 1 : "Enter the Calm / Start the tour"
 * 
 * DISCOVER HEAREN - BUILD 50 SPECS (STRICT):
 * - Son d'ambiance: Calm_031.mp3 (unique, continu, 30% volume, fade-in 800-1200ms)
 * - Voix Hera au tap: "Prenez un instant. Il n'y a rien que vous deviez faire maintenant."
 * - Le son ne doit PAS redémarrer entre les écrans
 * - Aucun autre son ou musique ajouté
 * 
 * iOS SAFARI FIX:
 * - audio.play() DOIT être la PREMIÈRE instruction dans le handler de tap
 * - On crée un objet Audio global (window.discoverVoiceAudio) déverrouillé au premier tap
 * - Cet objet sera réutilisé sur l'écran final (change src + play)
 */
const JourneyIntro = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const [showTitle, setShowTitle] = useState(false);
  const [showSubtext, setShowSubtext] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [audioStatus, setAudioStatus] = useState(''); // Debug status
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';

  useEffect(() => {
    setIsFullscreen(true);
    
    // Timeline d'animations
    setTimeout(() => setShowTitle(true), 500);
    setTimeout(() => setShowSubtext(true), 800);
    setTimeout(() => setShowButton(true), 1100);
    
    return () => setIsFullscreen(false);
  }, [setIsFullscreen]);

  const handleStart = async () => {
    if (isStarting) return; // Prevent double-tap
    setIsStarting(true);
    
    const baseUrl = getBaseUrl();
    const lang = isEnglish ? 'en' : 'fr';
    const isiOS = isIOS();
    
    console.log('═══════════════════════════════════════════════');
    console.log('[Discover] 🎯 BUILD 52.1 - Start tour tapped');
    console.log('[Discover] 📱 Platform:', isiOS ? 'iOS' : 'Non-iOS');
    console.log('[Discover] 🌐 BaseURL:', baseUrl);
    console.log('═══════════════════════════════════════════════');
    
    // ================================================================
    // HELPER: Verify playback actually started (iOS anti-faux-positif)
    // Checks: audio.paused === false AND currentTime progresses
    // ================================================================
    const verifyPlaybackStarted = (audio, label) => {
      return new Promise((resolve) => {
        // Check 1: After 400ms, verify not paused
        setTimeout(() => {
          const pausedCheck = audio.paused;
          const time1 = audio.currentTime;
          console.log(`[Discover] 🔍 [${label}] Check @400ms: paused=${pausedCheck}, currentTime=${time1.toFixed(3)}s`);
          
          if (pausedCheck) {
            console.error(`[Discover] ❌ [${label}] playback did not start: audio.paused=true`);
            resolve({ success: false, reason: 'paused=true after 400ms' });
            return;
          }
          
          // Check 2: After 1s total, verify currentTime progressed
          setTimeout(() => {
            const time2 = audio.currentTime;
            const progressed = time2 > 0.05;
            console.log(`[Discover] 🔍 [${label}] Check @1s: currentTime=${time2.toFixed(3)}s, progressed=${progressed}`);
            
            if (!progressed) {
              console.error(`[Discover] ❌ [${label}] playback did not start: currentTime not progressing (${time2.toFixed(3)}s)`);
              resolve({ success: false, reason: `currentTime stuck at ${time2.toFixed(3)}s` });
              return;
            }
            
            console.log(`[Discover] ✅ [${label}] VERIFIED: playback is real (currentTime=${time2.toFixed(3)}s)`);
            resolve({ success: true });
          }, 600); // 400ms + 600ms = 1s total
        }, 400);
      });
    };
    
    // ================================================================
    // BUILD 52 iOS FIX - MINIMAL & SAFE
    // Step 1: ALWAYS unlock audio first (even on non-iOS for consistency)
    // ================================================================
    try {
      console.log('[Discover] 🔓 Step 1: Unlocking audio...');
      await unlockAudioOnFirstTap();
      console.log('[Discover] ✅ Step 1: Audio unlocked');
    } catch (unlockErr) {
      console.error('[Discover] ❌ Step 1 FAILED: Audio unlock error:', unlockErr?.message || unlockErr);
      setAudioStatus('❌ Unlock failed: ' + (unlockErr?.message || 'Unknown'));
    }
    
    // ================================================================
    // Step 2: Create and START bed track (Calm_031.mp3) IMMEDIATELY
    // On iOS, play() must be called synchronously in user gesture
    // ================================================================
    const ambientUrl = `${baseUrl}/api/sos-calm/music/stream/Calm_031.mp3`;
    console.log('[Discover] 🎵 Step 2: Starting bed track:', ambientUrl);
    
    const ambientAudio = new Audio();
    ambientAudio.src = ambientUrl;
    ambientAudio.volume = 0; // Start at 0 for fade-in
    ambientAudio.loop = true;
    window.discoverAmbientAudio = ambientAudio;
    
    // BUILD 52.4: Attach monitoring for screen-lock debugging
    monitorAudioPlayback(ambientAudio, 'Discover-Bed');
    
    // CRITICAL: Call play() IMMEDIATELY (synchronous in gesture)
    let bedSuccess = false;
    let bedError = null;
    try {
      const bedPlayPromise = ambientAudio.play();
      if (bedPlayPromise) {
        await bedPlayPromise;
      }
      bedSuccess = true;
      console.log('[Discover] ✅ Step 2: Bed play() promise resolved');
      setAudioStatus('🎵 Bed: play() OK');
      
      // Fade-in to 30% volume over 1000ms
      let step = 0;
      const fadeIn = setInterval(() => {
        step++;
        ambientAudio.volume = Math.min(step * 0.015, 0.3); // 20 steps to 0.3
        if (step >= 20) {
          clearInterval(fadeIn);
          console.log('[Discover] ✅ Bed fade-in complete (30%)');
        }
      }, 50);
    } catch (bedErr) {
      bedError = bedErr;
      console.error('[Discover] ❌ Step 2 FAILED: Bed track error:', bedErr?.name, bedErr?.message);
      setAudioStatus('❌ Bed: ' + (bedErr?.message || bedErr?.name || 'Failed'));
    }
    
    // ================================================================
    // Step 3: Create and START Hera voice
    // ================================================================
    const heraUrl = `${baseUrl}/api/voice-files/discover_start_${lang}.mp3`;
    console.log('[Discover] 🎙️ Step 3: Starting Hera voice:', heraUrl);
    
    const heraAudio = new Audio();
    heraAudio.src = heraUrl;
    heraAudio.volume = 0.8;
    window.discoverVoiceAudio = heraAudio;
    window.discoverIntroVoiceEnded = false;
    
    // BUILD 52.4: Attach monitoring for screen-lock debugging
    monitorAudioPlayback(heraAudio, 'Discover-Voice');
    
    heraAudio.onended = () => {
      console.log('[Discover] ✅ Hera intro voice ENDED');
      window.discoverIntroVoiceEnded = true;
    };
    
    heraAudio.onerror = (e) => {
      const err = heraAudio.error;
      console.error('[Discover] ❌ Hera voice error event:', err?.code, err?.message);
      window.discoverIntroVoiceEnded = true;
    };
    
    // CRITICAL: Call play() IMMEDIATELY
    let voiceSuccess = false;
    let voiceError = null;
    try {
      const voicePlayPromise = heraAudio.play();
      if (voicePlayPromise) {
        await voicePlayPromise;
      }
      voiceSuccess = true;
      console.log('[Discover] ✅ Step 3: Hera voice play() promise resolved');
      setAudioStatus(prev => prev + ' | 🎙️ Voice: play() OK');
    } catch (voiceErr) {
      voiceError = voiceErr;
      console.error('[Discover] ❌ Step 3 FAILED: Hera voice error:', voiceErr?.name, voiceErr?.message);
      setAudioStatus(prev => prev + ' | ❌ Voice: ' + (voiceErr?.message || voiceErr?.name || 'Failed'));
      window.discoverIntroVoiceEnded = true;
    }
    
    // ================================================================
    // Step 4: VERIFY playback actually started (iOS anti-faux-positif)
    // This proves audio is REALLY playing, not just play() resolved
    // ================================================================
    console.log('[Discover] 🔍 Step 4: Verifying real playback...');
    
    // Verify bed track
    let bedVerified = false;
    if (bedSuccess) {
      const bedCheck = await verifyPlaybackStarted(ambientAudio, 'BED');
      bedVerified = bedCheck.success;
      if (!bedVerified) {
        setAudioStatus(prev => prev.replace('Bed: play() OK', `Bed FAILED: ${bedCheck.reason}`));
      } else {
        setAudioStatus(prev => prev.replace('Bed: play() OK', 'Bed ✅ VERIFIED'));
      }
    }
    
    // Verify voice track
    let voiceVerified = false;
    if (voiceSuccess) {
      const voiceCheck = await verifyPlaybackStarted(heraAudio, 'VOICE');
      voiceVerified = voiceCheck.success;
      if (!voiceVerified) {
        setAudioStatus(prev => prev.replace('Voice: play() OK', `Voice FAILED: ${voiceCheck.reason}`));
      } else {
        setAudioStatus(prev => prev.replace('Voice: play() OK', 'Voice ✅ VERIFIED'));
      }
    }
    
    // ================================================================
    // Step 5: Final status with VERIFIED results + UI error message
    // ================================================================
    console.log('═══════════════════════════════════════════════');
    console.log('[Discover] 📊 FINAL STATUS (VERIFIED):');
    console.log('[Discover]    Bed track:', bedVerified ? '✅ VERIFIED PLAYING' : `❌ NOT PLAYING (play()=${bedSuccess})`);
    console.log('[Discover]    Hera voice:', voiceVerified ? '✅ VERIFIED PLAYING' : `❌ NOT PLAYING (play()=${voiceSuccess})`);
    console.log('═══════════════════════════════════════════════');
    
    // BUILD 52.4: Log full diagnostic state for TestFlight
    logAudioDiagnostics(ambientAudio, null, 'Discover-AfterVerification');
    
    // Show UI error if audio failed (temporary for TestFlight debugging)
    const failures = [];
    if (!bedVerified) {
      failures.push(`Bed: ${bedError?.message || bedError?.name || 'playback not started'}`);
    }
    if (!voiceVerified) {
      failures.push(`Voice: ${voiceError?.message || voiceError?.name || 'playback not started'}`);
    }
    
    if (failures.length > 0) {
      const errorMsg = `Discover audio failed: ${failures.join(', ')}`;
      console.error('[Discover] ❌ AUDIO FAILURE:', errorMsg);
      setAudioStatus(`❌ ${errorMsg}`);
      // Keep error visible for 5 seconds before navigating
      await new Promise(r => setTimeout(r, 3000));
    }
    
    // Navigate after verification complete
    setTimeout(() => {
      console.log('[Discover] ➡️ Navigating to breathing cycle');
      navigate('/journey-breathing-cycle');
    }, 500);
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center px-6"
      style={{
        background: 'linear-gradient(180deg, #667eea 0%, #4338ca 50%, #1e1b4b 100%)',
        animation: 'gradient-vertical-shift 12s ease-in-out infinite',
      }}
    >
      {/* Orbe lumineuse centrale */}
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        style={{
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, rgba(99, 102, 241, 0.2) 40%, transparent 70%)',
          filter: 'blur(80px)',
          animation: 'orb-gentle-pulse 7s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      {/* Particules légères */}
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `particle-vertical-drift ${8 + Math.random() * 4}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 4}s`,
            opacity: 0.1,
            pointerEvents: 'none',
          }}
        >
          <Sparkles className="w-2 h-2 text-white" />
        </div>
      ))}

      {/* Contenu */}
      <div className="relative z-10 flex flex-col items-center max-w-lg">
        {/* Icône centrale */}
        <div 
          className="mb-10 relative"
          style={{ animation: 'float-gentle 6s ease-in-out infinite' }}
        >
          <div 
            className="w-32 h-32 rounded-full flex items-center justify-center shadow-2xl"
            style={{
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
            }}
          >
            <Sparkles className="w-16 h-16 text-white" />
          </div>
        </div>

        {/* Titre */}
        <h1 
          className="text-4xl font-bold text-white mb-6 text-center transition-all duration-300"
          style={{
            opacity: showTitle ? 1 : 0,
            transform: showTitle ? 'translateY(0)' : 'translateY(20px)',
            textShadow: '0 2px 20px rgba(139, 92, 246, 0.5)',
          }}
        >
          {isEnglish ? 'Enter the Calm' : 'Entrer dans le calme'}
        </h1>

        {/* Sous-texte */}
        <p 
          className="text-white/90 text-center text-lg mb-12 leading-relaxed max-w-md transition-all duration-300"
          style={{
            opacity: showSubtext ? 1 : 0,
            transform: showSubtext ? 'translateY(0)' : 'translateY(20px)',
          }}
        >
          {isEnglish
            ? 'Discover how Hearen supports you in your well-being.'
            : 'Découvrez comment Hearen vous accompagne dans votre apaisement.'
          }
        </p>

        {/* CTA - Start the tour */}
        <button
          onClick={handleStart}
          disabled={isStarting}
          className="relative px-10 py-4 bg-white text-purple-700 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-70"
          style={{
            opacity: showButton ? 1 : 0,
            transform: showButton ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
            boxShadow: '0 0 40px rgba(139, 92, 246, 0.6), 0 8px 20px rgba(139, 92, 246, 0.4)',
            animation: showButton && !isStarting ? 'button-vibration 2s ease-in-out infinite' : 'none',
          }}
        >
          {isStarting ? (isEnglish ? 'Loading...' : 'Chargement...') : (isEnglish ? 'Start the tour' : 'Commencer la visite')}
        </button>
        
        {/* Debug indicator removed - BUILD 66 */}
      </div>

      <style>{`
        @keyframes gradient-vertical-shift {
          0%, 100% { background-position: 0% 0%; }
          50% { background-position: 0% 100%; }
        }
        
        @keyframes orb-gentle-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.4; }
          50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.6; }
        }
        
        @keyframes particle-vertical-drift {
          0% { transform: translateY(0); opacity: 0.1; }
          50% { opacity: 0.15; }
          100% { transform: translateY(-60px); opacity: 0; }
        }
        
        @keyframes float-gentle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes button-vibration {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(0.98); }
        }
      `}</style>
    </div>
  );
};

export default JourneyIntro;
